import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import {
  getGatewayBaseUrl,
  runtimeConfig,
  setDevelopmentGatewayBaseUrl,
} from "../runtime/config";
import {
  type EmployeeLoginResult,
  type EmployeeRuntimeActor,
  PosIdentityGatewayClient,
} from "../runtime/gateway/identity-client";
import { createHardwareRuntime } from "../runtime/hardware/registry";
import {
  clearLocalEmployeeSession,
  markEmployeeSessionLocal,
  readLocalEmployeeSession,
  saveCoreEmployeeSession,
  type LocalEmployeeSessionState,
} from "../runtime/local-store/employee-session";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import {
  clearLocalShift,
  readLocalShift,
  saveCoreShift,
  type LocalShiftState,
} from "../runtime/local-store/shift-state";
import {
  readTerminalCredential,
  savePendingTerminalSecret,
  saveTrustedTerminal,
  type TerminalCredentialState,
} from "../runtime/local-store/terminal-state";
import { useRuntimeAutoSync } from "../runtime/sync/use-runtime-auto-sync";
import { PosWorkspace } from "./PosWorkspace";
import { ShiftOpenRuntimeModal } from "./ShiftOpenRuntimeModal";

type CheckState = "checking" | "ready" | "attention";
type TerminalVerification = "none" | "checking" | "cached" | "verified" | "invalid";
type SessionVerification = "none" | "checking" | "cached" | "verified" | "invalid";

type RuntimeCheck = {
  label: string;
  detail: string;
  state: CheckState;
};

type ActivationForm = {
  organizationCode: string;
  nodeCode: string;
  username: string;
  password: string;
};

type LoginForm = {
  employeeCode: string;
  pin: string;
};

type ShiftOpenForm = {
  openingCash: string;
  pin: string;
};

let terminalCredentialEpoch = 0;

function requestKey(prefix: string) {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function actorFromLoginResult(result: EmployeeLoginResult): EmployeeRuntimeActor {
  const {
    runtime_session_token: _runtimeSessionToken,
    request_key: _requestKey,
    idempotent_replay: _idempotentReplay,
    ...actor
  } = result;
  return actor;
}

function employeeRuntimeSignature(actor: EmployeeRuntimeActor) {
  return JSON.stringify({
    organization_id: actor.organization_id,
    node_id: actor.node_id,
    branch_id: actor.branch_id,
    employee_id: actor.employee_id,
    employee_code: actor.employee_code,
    employee_name: actor.employee_name,
    employee_credential_version: actor.employee_credential_version,
    device_credential_id: actor.device_credential_id,
    device_credential_version: actor.device_credential_version,
    permission_codes: [...actor.permission_codes].sort(),
    runtime_session_id: actor.runtime_session_id,
    runtime_session_status: actor.runtime_session_status,
  });
}

function StatusDot({ state }: { state: CheckState }) {
  return <span className={`status-dot status-dot--${state}`} aria-hidden="true" />;
}

function errorText(value: string) {
  if (value.includes("GATEWAY_UNREACHABLE")) return "ติดต่อ MORE POS Gateway ไม่ได้";
  if (value.includes("GATEWAY_TIMEOUT")) return "MORE POS Gateway ตอบกลับช้าเกินไป";
  if (value.includes("POS_ORIGIN_NOT_ALLOWED")) return "Gateway ยังไม่อนุญาต Runtime นี้";
  if (value.includes("INVALID_CREDENTIALS")) return "บัญชีผู้ดูแลหรือรหัสผ่านไม่ถูกต้อง";
  if (value.includes("COMMERCE_NODE_NOT_FOUND")) return "ไม่พบจุดใช้งานนี้ในองค์กร กรุณาสร้าง Commerce Node ใน Back Office ก่อน";
  if (value.includes("ACTIVE_NODE_RUNTIME_CREDENTIAL_ALREADY_EXISTS")) return "จุดใช้งานนี้มีเครื่องที่ Activate อยู่แล้ว หากเป็นเครื่องนี้ให้ใช้ Re-activate เพื่อยกเลิก Credential เดิม";
  if (value.includes("POS_IDENTITY_INVALID") || value.includes("DEVICE_CREDENTIAL_INVALID")) return "Terminal Credential ใช้ไม่ได้หรือถูกยกเลิก ต้อง Activate เครื่องใหม่";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานเดิมใช้ไม่ได้แล้ว กรุณายืนยันพนักงานด้วย PIN ใหม่";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_REQUEST_KEY_CONFLICT")) return "คำขอ Login นี้ขัดกับ Session เดิม กรุณาลองใหม่";
  if (value.includes("INVALID_EMPLOYEE_CREDENTIAL") || value.includes("POS_SHIFT_EMPLOYEE_NOT_AUTHORIZED")) return "รหัสพนักงานหรือ PIN ไม่ถูกต้อง หรือพนักงานไม่มีสิทธิ์ในสาขานี้";
  if (value.includes("POS_SHIFT_PERMISSION_DENIED")) return "พนักงานคนนี้เข้า POS ได้ แต่ Policy ขององค์กรไม่อนุญาตให้เปิดกะ";
  if (value.includes("POS_TERMINAL_ALREADY_HAS_ACTIVE_SHIFT")) return "เครื่องนี้มีกะเปิดอยู่แล้ว ระบบจะอ่านกะเดิมกลับจาก Core";
  if (value.includes("OPENING_CASH_INVALID")) return "เงินตั้งต้นต้องเป็นจำนวนตั้งแต่ 0 บาทขึ้นไป";
  if (value.includes("POS_SHIFT_REQUEST_KEY_CONFLICT")) return "คำขอเปิดกะนี้ถูกใช้กับข้อมูลอื่นแล้ว กรุณาลองใหม่";
  if (value.includes("PERMISSION")) return "บัญชีนี้ไม่มีสิทธิ์ทำรายการนี้";
  return value || "ทำรายการไม่สำเร็จ";
}

function isAuthoritativeTerminalInvalid(value: string | undefined) {
  return Boolean(value && (
    value.includes("POS_IDENTITY_INVALID") ||
    value.includes("DEVICE_CREDENTIAL_INVALID")
  ));
}

function isAuthoritativeSessionInvalid(value: string | undefined) {
  return Boolean(value && value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID"));
}

export function App() {
  const [localStoreReady, setLocalStoreReady] = useState(false);
  const [localStoreBootError, setLocalStoreBootError] = useState(false);
  const [localStoreBootStage, setLocalStoreBootStage] = useState("T1 BOOT 0 · รอเริ่ม Local Runtime");
  const [online, setOnline] = useState(navigator.onLine);
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState(() => getGatewayBaseUrl());
  const [gatewayDraft, setGatewayDraft] = useState(() => getGatewayBaseUrl() ?? "");
  const [terminal, setTerminal] = useState<TerminalCredentialState | null>(null);
  const [terminalVerification, setTerminalVerification] = useState<TerminalVerification>("none");
  const [employeeSession, setEmployeeSession] = useState<LocalEmployeeSessionState | null>(null);
  const [sessionVerification, setSessionVerification] = useState<SessionVerification>("none");
  const [shiftState, setShiftState] = useState<LocalShiftState | null>(null);
  const [actor, setActor] = useState<EmployeeRuntimeActor | null>(null);
  const [activation, setActivation] = useState<ActivationForm>({
    organizationCode: "",
    nodeCode: "",
    username: "",
    password: "",
  });
  const [login, setLogin] = useState<LoginForm>({ employeeCode: "", pin: "" });
  const [shiftOpenForm, setShiftOpenForm] = useState<ShiftOpenForm>({ openingCash: "", pin: "" });
  const [showShiftOpen, setShowShiftOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowRotate, setAllowRotate] = useState(false);
  const [activationStage, setActivationStage] = useState<string | null>(null);

  const hardware = useMemo(() => createHardwareRuntime(), []);
  const gateway = useMemo(
    () => (gatewayBaseUrl ? new PosIdentityGatewayClient({ baseUrl: gatewayBaseUrl }) : null),
    [gatewayBaseUrl],
  );

  useEffect(() => {
    let active = true;

    void (async () => {
      try {
        const withBootTimeout = <T,>(work: Promise<T>, code: string) =>
          Promise.race<T>([
            work,
            new Promise<T>((_resolve, reject) => {
              window.setTimeout(() => reject(new Error(code)), 12_000);
            }),
          ]);

        setLocalStoreBootStage("T1 BOOT 1 · กำลังเปิด Runtime DB");
        await withBootTimeout(posRuntimeDb.open(), "POS_RUNTIME_DB_OPEN_TIMEOUT");

        setLocalStoreBootStage("T1 BOOT 2 · กำลังอ่านข้อมูล Local");
        const [storedTerminal, storedEmployeeSession, storedShift] = await withBootTimeout(Promise.all([
          readTerminalCredential(),
          readLocalEmployeeSession(),
          readLocalShift(),
        ]), "POS_RUNTIME_DB_READ_TIMEOUT");

        setLocalStoreBootStage("T1 BOOT 3 · อ่าน Local Runtime สำเร็จ");

        if (!active) return;
        if (storedTerminal) {
          setTerminal(storedTerminal);
          setTerminalVerification(storedTerminal.identity ? "cached" : "checking");
        }

        if (
          storedEmployeeSession &&
          storedTerminal?.identity &&
          storedEmployeeSession.actor.organization_id === storedTerminal.identity.organization_id &&
          storedEmployeeSession.actor.node_id === storedTerminal.identity.node_id &&
          storedEmployeeSession.actor.branch_id === storedTerminal.identity.branch_id
        ) {
          const localSession: LocalEmployeeSessionState = {
            ...storedEmployeeSession,
            source: "LOCAL",
          };
          setEmployeeSession(localSession);
          setActor(localSession.actor);
          setSessionVerification("cached");
        } else if (storedEmployeeSession) {
          await clearLocalEmployeeSession();
        }

        if (
          storedShift &&
          storedTerminal?.identity &&
          storedShift.shift.node_id === storedTerminal.identity.node_id &&
          storedShift.shift.branch_id === storedTerminal.identity.branch_id
        ) {
          setShiftState({ ...storedShift, source: "LOCAL" });
        } else if (storedShift) {
          await clearLocalShift();
        }
      } catch (bootError) {
        if (active) {
          setLocalStoreBootError(true);
          setLocalStoreBootStage(
            bootError instanceof Error ? bootError.message : "POS_RUNTIME_DB_BOOT_FAILED",
          );
        }
      } finally {
        if (active) setLocalStoreReady(true);
      }
    })();

    const updateOnline = () => setOnline(navigator.onLine);
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);

    return () => {
      active = false;
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);

  const verifyStoredTerminal = useCallback(async () => {
    if (!gateway || !terminal?.deviceSecret || !online) return;

    const deviceSecret = terminal.deviceSecret;
    const verificationEpoch = terminalCredentialEpoch;
    setTerminalVerification("checking");
    const result = await gateway.resolveTerminal(deviceSecret);

    // A ROTATE/ISSUE may have replaced the terminal credential while this read-back was
    // in flight. Never let an older response invalidate or overwrite the newer credential.
    if (verificationEpoch !== terminalCredentialEpoch) return;

    if (!result.ok || !result.data) {
      const rawError = result.error;
      if (!isAuthoritativeTerminalInvalid(rawError)) {
        setTerminalVerification(terminal.identity ? "cached" : "invalid");
        setError(null);
        return;
      }
      setTerminalVerification("invalid");
      await clearLocalEmployeeSession();
      setEmployeeSession(null);
      setSessionVerification("invalid");
      setActor(null);
      setError(errorText(rawError ?? "POS_IDENTITY_INVALID"));
      return;
    }

    try {
      await saveTrustedTerminal(deviceSecret, result.data);
    } catch (persistError) {
      if (persistError instanceof Error && persistError.message === "TERMINAL_CREDENTIAL_SUPERSEDED") return;
      throw persistError;
    }
    if (verificationEpoch !== terminalCredentialEpoch) return;

    setTerminal({
      deviceSecret,
      identity: result.data,
      verifiedAt: new Date().toISOString(),
    });
    setTerminalVerification("verified");
    setError(null);
  }, [gateway, online, terminal?.deviceSecret, terminal?.identity]);

  const resumeStoredEmployeeSession = useCallback(async () => {
    if (
      !gateway ||
      !terminal?.deviceSecret ||
      !terminal.identity ||
      !online ||
      !employeeSession?.sessionToken
    ) {
      return;
    }

    setSessionVerification("checking");
    const result = await gateway.resumeEmployeeSession({
      deviceSecret: terminal.deviceSecret,
      sessionToken: employeeSession.sessionToken,
    });

    if (!result.ok || !result.data) {
      const rawError = result.error;
      if (!isAuthoritativeSessionInvalid(rawError)) {
        const localSession: LocalEmployeeSessionState = {
          ...employeeSession,
          source: "LOCAL",
        };
        setEmployeeSession(localSession);
        setSessionVerification("cached");
        setActor(localSession.actor);
        setError(null);
        void markEmployeeSessionLocal(localSession);
        return;
      }
      await clearLocalEmployeeSession();
      setEmployeeSession(null);
      setSessionVerification("invalid");
      setActor(null);
      setLogin({ employeeCode: "", pin: "" });
      setError(errorText(rawError ?? "POS_EMPLOYEE_RUNTIME_SESSION_INVALID"));
      return;
    }

    const verifiedAt = new Date().toISOString();
    await saveCoreEmployeeSession(employeeSession.sessionToken, result.data);
    setEmployeeSession({
      sessionToken: employeeSession.sessionToken,
      actor: result.data,
      source: "CORE",
      verifiedAt,
    });
    setActor(result.data);
    setSessionVerification("verified");
    setError(null);
  }, [employeeSession, gateway, online, terminal?.deviceSecret, terminal?.identity]);

  const refreshActiveEmployeeSession = useCallback(async () => {
    const sessionToken = employeeSession?.sessionToken;
    if (!gateway || !terminal?.deviceSecret || !terminal.identity || !online || !sessionToken) return;

    const result = await gateway.resumeEmployeeSession({
      deviceSecret: terminal.deviceSecret,
      sessionToken,
    });

    if (!result.ok || !result.data) {
      if (!isAuthoritativeSessionInvalid(result.error)) return;
      const latest = await readLocalEmployeeSession();
      if (!latest || latest.sessionToken !== sessionToken) return;
      await clearLocalEmployeeSession();
      setEmployeeSession(null);
      setSessionVerification("invalid");
      setActor(null);
      setLogin({ employeeCode: "", pin: "" });
      setShiftOpenForm({ openingCash: "", pin: "" });
      setShowShiftOpen(false);
      setError(errorText(result.error ?? "POS_EMPLOYEE_RUNTIME_SESSION_INVALID"));
      return;
    }

    const latest = await readLocalEmployeeSession();
    if (!latest || latest.sessionToken !== sessionToken) return;
    if (employeeRuntimeSignature(latest.actor) === employeeRuntimeSignature(result.data)) return;

    const verifiedAt = new Date().toISOString();
    await saveCoreEmployeeSession(sessionToken, result.data);
    setEmployeeSession({
      sessionToken,
      actor: result.data,
      source: "CORE",
      verifiedAt,
    });
    setActor(result.data);
    setSessionVerification("verified");
    setError(null);
  }, [employeeSession?.sessionToken, gateway, online, terminal?.deviceSecret, terminal?.identity]);

  useRuntimeAutoSync({
    enabled: Boolean(
      terminalVerification === "verified" &&
      sessionVerification === "verified" &&
      online &&
      gateway &&
      terminal?.deviceSecret &&
      terminal.identity &&
      employeeSession?.sessionToken &&
      actor?.runtime_session_id
    ),
    sync: refreshActiveEmployeeSession,
    triggerKey: actor?.runtime_session_id ?? "",
  });

  const refreshShiftRuntime = useCallback(async () => {
    if (!gateway || !terminal?.deviceSecret || !online) return;

    const result = await gateway.getCashShiftRuntime(terminal.deviceSecret);
    if (!result.ok || !result.data) {
      setError(errorText(result.error ?? "POS_SHIFT_RUNTIME_LOAD_FAILED"));
      return;
    }

    if (result.data.active_shift) {
      await saveCoreShift(result.data.active_shift);
      setShiftState({
        shift: result.data.active_shift,
        source: "CORE",
        verifiedAt: new Date().toISOString(),
      });
    } else {
      await clearLocalShift();
      setShiftState(null);
    }
  }, [gateway, online, terminal?.deviceSecret]);

  useEffect(() => {
    if (!localStoreReady || !terminal?.deviceSecret || !gateway || !online) return;
    void verifyStoredTerminal();
  }, [gateway, localStoreReady, online, terminal?.deviceSecret, verifyStoredTerminal]);

  useEffect(() => {
    if (
      terminalVerification !== "verified" ||
      !online ||
      !employeeSession?.sessionToken ||
      sessionVerification === "verified" ||
      sessionVerification === "checking"
    ) {
      return;
    }
    void resumeStoredEmployeeSession();
  }, [
    employeeSession?.sessionToken,
    online,
    resumeStoredEmployeeSession,
    sessionVerification,
    terminalVerification,
  ]);

  useEffect(() => {
    if (terminalVerification !== "verified" || !online) return;
    void refreshShiftRuntime();
  }, [online, refreshShiftRuntime, terminalVerification]);

  useEffect(() => {
    if (!online && shiftState) {
      setShiftState((current) => current ? { ...current, source: "LOCAL" } : current);
    }

    if (!online && employeeSession) {
      const localSession: LocalEmployeeSessionState = {
        ...employeeSession,
        source: "LOCAL",
      };
      setEmployeeSession(localSession);
      setSessionVerification("cached");
      void markEmployeeSessionLocal(localSession);
    }
  }, [online]);

  function applyDevelopmentGateway(event: FormEvent) {
    event.preventDefault();
    const value = gatewayDraft.trim();
    setDevelopmentGatewayBaseUrl(value);
    const resolved = getGatewayBaseUrl();
    setGatewayBaseUrl(resolved);
    setError(resolved ? null : "กรุณาระบุ Gateway URL สำหรับการทดสอบ Local");
  }

  function submitTerminalActivation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void activateTerminal("ISSUE");
  }

  function showActivationStage(message: string) {
    setActivationStage(message);
    const status = document.querySelector<HTMLElement>("[data-pos-activation-stage]");
    if (status) status.textContent = message;
  }

  async function activateTerminal(
    action: "ISSUE" | "ROTATE",
    submittedActivation: ActivationForm = activation,
  ) {
    if (!gateway) {
      setError("ยังไม่ได้กำหนด MORE POS Gateway");
      return;
    }
    if (!online) {
      setError("การ Activate Terminal ครั้งแรกต้องเชื่อมต่ออินเทอร์เน็ต");
      return;
    }
    if (
      !submittedActivation.organizationCode.trim() ||
      !submittedActivation.nodeCode.trim() ||
      !submittedActivation.username.trim() ||
      !submittedActivation.password
    ) {
      setError("กรุณากรอกข้อมูล Activation ให้ครบ");
      return;
    }

    setBusy(true);
    setError(null);
    setAllowRotate(false);
    showActivationStage("T1 STEP 2 · กำลังติดต่อ Gateway");

    const enrollment = await gateway.enrollTerminal({
      organizationCode: submittedActivation.organizationCode.trim(),
      username: submittedActivation.username.trim(),
      password: submittedActivation.password,
      nodeCode: submittedActivation.nodeCode.trim(),
      action,
      requestKey: requestKey(`pos-terminal:${action.toLowerCase()}`),
      clientContext: {
        runtime: "WEBPOS",
        origin: window.location.origin,
        build_mode: runtimeConfig.buildMode,
      },
    });

    if (!enrollment.ok || !enrollment.data) {
      const rawError = enrollment.error ?? "POS_TERMINAL_ENROLLMENT_FAILED";
      showActivationStage("T1 STOP · Gateway ปฏิเสธคำขอ");
      setError(errorText(rawError));
      setAllowRotate(rawError.includes("ACTIVE_NODE_RUNTIME_CREDENTIAL_ALREADY_EXISTS"));
      setActivation((current) => ({ ...current, password: "" }));
      setBusy(false);
      return;
    }

    const deviceSecret = enrollment.data.device_secret ?? "";
    if (!deviceSecret || enrollment.data.secret_available !== true) {
      setError("ไม่ได้รับ Device Secret ใหม่ กรุณา Re-activate ด้วย Request ใหม่");
      setAllowRotate(true);
      setActivation((current) => ({ ...current, password: "" }));
      setBusy(false);
      return;
    }

    // Invalidate every verification that started with the previous secret before any
    // local await. A late vN response must never overwrite the just-issued vN+1 secret.
    terminalCredentialEpoch += 1;

    try {
      await savePendingTerminalSecret(deviceSecret);
    } catch {
      setActivation((current) => ({ ...current, password: "" }));
      setError("Core ออก Terminal Credential ใหม่แล้ว แต่บันทึกลงเครื่องไม่สำเร็จ กรุณาอย่าปิด POS และให้ผู้ดูแลตรวจ Local Storage ก่อน Re-activate ซ้ำ");
      setBusy(false);
      return;
    }
    setTerminal({ deviceSecret, identity: null, verifiedAt: null });

    const readback = await gateway.resolveTerminal(deviceSecret);
    if (!readback.ok || !readback.data) {
      setTerminalVerification("invalid");
      setActivation((current) => ({ ...current, password: "" }));
      setError(errorText(readback.error ?? "POS_IDENTITY_INVALID"));
      setBusy(false);
      return;
    }

    try {
      await saveTrustedTerminal(deviceSecret, readback.data);
    } catch {
      setActivation((current) => ({ ...current, password: "" }));
      setError("ยืนยัน Terminal กับ Core สำเร็จ แต่ Local Read-after-write ไม่ผ่าน กรุณาอย่า Re-activate ซ้ำจนกว่าจะตรวจ Local Storage");
      setBusy(false);
      return;
    }
    await Promise.all([clearLocalEmployeeSession(), clearLocalShift()]);
    setTerminal({ deviceSecret, identity: readback.data, verifiedAt: new Date().toISOString() });
    setTerminalVerification("verified");
    setEmployeeSession(null);
    setSessionVerification("none");
    setShiftState(null);
    setActor(null);
    setShowShiftOpen(false);
    setActivation({ organizationCode: "", nodeCode: "", username: "", password: "" });
    setLogin({ employeeCode: "", pin: "" });
    showActivationStage("T1 READY · Activation สำเร็จ กำลังเปิด POS");
    setBusy(false);
    if ((navigator.userAgent || "").includes("MOREPOSHybrid/")) {
      window.setTimeout(() => window.location.reload(), 0);
    }
  }

  function bindLegacyActivationButton(button: HTMLButtonElement | null) {
    if (!button) return;

    // Commit the native handler with the DOM node itself. Older WebViews can paint the
    // activation surface before a passive effect binds, leaving a visible but inert button.
    button.onclick = (event) => {
      if (button.disabled || busy) return;

      event.preventDefault();
      showActivationStage("T1 STEP 1 · รับการกดแล้ว");

      try {
        const form = button.form;
        if (!form) throw new Error("ACTIVATION_FORM_NOT_FOUND");

        const fieldValue = (name: string) => {
          const field = form.elements.namedItem(name);
          return field instanceof HTMLInputElement ? field.value : "";
        };

        void activateTerminal("ISSUE", {
          organizationCode: fieldValue("organizationCode"),
          nodeCode: fieldValue("nodeCode"),
          username: fieldValue("username"),
          password: fieldValue("password"),
        });
      } catch (activationError) {
        setBusy(false);
        showActivationStage("T1 STOP · อ่านข้อมูลจากฟอร์มไม่ได้");
        setError(errorText(activationError instanceof Error ? activationError.message : "ACTIVATION_FORM_READ_FAILED"));
      }
    };
  }

  async function loginEmployee(event: FormEvent) {
    event.preventDefault();
    if (!gateway || !terminal?.deviceSecret || !terminal.identity || terminalVerification !== "verified") {
      setError("Terminal ยังไม่พร้อม จึง Login พนักงานไม่ได้");
      return;
    }
    if (!online) {
      setError("Offline Employee Re-entry ยังต้องรอ Published Offline Access Policy ก่อน");
      return;
    }
    if (!login.employeeCode.trim() || !login.pin.trim()) {
      setError("กรุณากรอกรหัสพนักงานและ PIN");
      return;
    }

    setBusy(true);
    setError(null);
    const result = await gateway.loginEmployee({
      deviceSecret: terminal.deviceSecret,
      employeeCode: login.employeeCode.trim(),
      pin: login.pin.trim(),
      requestKey: requestKey("pos-frontline-login"),
      clientContext: {
        runtime: "WEBPOS",
        origin: window.location.origin,
        terminal_node_id: terminal.identity.node_id,
      },
    });

    if (!result.ok || !result.data) {
      setError(errorText(result.error ?? "INVALID_EMPLOYEE_CREDENTIAL"));
      setLogin((current) => ({ ...current, pin: "" }));
      setBusy(false);
      return;
    }

    if (!result.data.runtime_session_token || !result.data.runtime_session_id) {
      setError("Gateway ไม่ได้คืน Employee Runtime Session ที่ใช้งานได้");
      setLogin({ employeeCode: "", pin: "" });
      setBusy(false);
      return;
    }

    const nextActor = actorFromLoginResult(result.data);
    const verifiedAt = new Date().toISOString();
    await saveCoreEmployeeSession(result.data.runtime_session_token, nextActor);
    setEmployeeSession({
      sessionToken: result.data.runtime_session_token,
      actor: nextActor,
      source: "CORE",
      verifiedAt,
    });
    setSessionVerification("verified");
    setActor(nextActor);
    setLogin({ employeeCode: "", pin: "" });
    setShiftOpenForm({ openingCash: "", pin: "" });
    setShowShiftOpen(false);
    setBusy(false);
  }

  async function endEmployeeRuntimeSession(endReason: "EXPLICIT_LOGOUT" | "FAST_SWITCH") {
    if (!gateway || !terminal?.deviceSecret || !terminal.identity || !employeeSession?.sessionToken) {
      await clearLocalEmployeeSession();
      setEmployeeSession(null);
      setSessionVerification("none");
      setActor(null);
      setLogin({ employeeCode: "", pin: "" });
      setShiftOpenForm({ openingCash: "", pin: "" });
      setShowShiftOpen(false);
      return;
    }

    if (!online) {
      setError("การจบ Employee Session ตอนนี้ต้อง Online เพื่อให้ Access Core ยืนยันการ Logout/Fast Switch");
      return;
    }

    setBusy(true);
    setError(null);
    const result = await gateway.endEmployeeSession({
      deviceSecret: terminal.deviceSecret,
      sessionToken: employeeSession.sessionToken,
      endReason,
      requestKey: requestKey(
        endReason === "FAST_SWITCH" ? "pos-frontline-fast-switch" : "pos-frontline-logout",
      ),
      clientContext: {
        runtime: "WEBPOS",
        origin: window.location.origin,
        terminal_node_id: terminal.identity.node_id,
        current_actor_employee_id: actor?.employee_id ?? employeeSession.actor.employee_id,
      },
    });

    if (!result.ok) {
      const rawError = result.error ?? "POS_EMPLOYEE_RUNTIME_SESSION_END_FAILED";
      if (rawError.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) {
        await clearLocalEmployeeSession();
        setEmployeeSession(null);
        setSessionVerification("invalid");
        setActor(null);
        setLogin({ employeeCode: "", pin: "" });
        setShiftOpenForm({ openingCash: "", pin: "" });
        setShowShiftOpen(false);
        setError(errorText(rawError));
      } else {
        setError(errorText(rawError));
      }
      setBusy(false);
      return;
    }

    await clearLocalEmployeeSession();
    setEmployeeSession(null);
    setSessionVerification("none");
    setActor(null);
    setLogin({ employeeCode: "", pin: "" });
    setShiftOpenForm({ openingCash: "", pin: "" });
    setShowShiftOpen(false);
    setBusy(false);
  }

  async function openShift(event: FormEvent) {
    event.preventDefault();
    if (!gateway || !terminal?.deviceSecret || !terminal.identity || terminalVerification !== "verified" || !actor) {
      setError("ต้อง Login พนักงานใน POS ก่อนเปิดกะ");
      return;
    }
    if (!online) {
      setError("การเปิดกะใน STEP 1.2A ต้อง Online เพื่อให้ Core เป็น Final Authority");
      return;
    }
    if (shiftState?.shift) {
      setError("เครื่องนี้มีกะเปิดอยู่แล้ว");
      return;
    }

    const openingCashText = shiftOpenForm.openingCash.trim();
    if (!shiftOpenForm.pin.trim()) {
      setError("กรุณาใส่ PIN เพื่อยืนยันการเปิดกะ");
      return;
    }
    if (!openingCashText) {
      setError("กรุณายืนยันเงินสดตั้งต้น หากไม่มีเงินสดให้ใส่ 0");
      return;
    }

    const openingCash = Number(openingCashText);
    if (!Number.isFinite(openingCash) || openingCash < 0) {
      setError("เงินตั้งต้นต้องเป็นจำนวนตั้งแต่ 0 บาทขึ้นไป");
      return;
    }

    setBusy(true);
    setError(null);
    const result = await gateway.openCashShift({
      deviceSecret: terminal.deviceSecret,
      employeeCode: actor.employee_code,
      pin: shiftOpenForm.pin.trim(),
      openingCash,
      requestKey: requestKey("pos-shift-open"),
      clientContext: {
        runtime: "WEBPOS",
        origin: window.location.origin,
        terminal_node_id: terminal.identity.node_id,
        current_actor_employee_id: actor.employee_id,
      },
    });

    if (!result.ok || !result.data?.active_shift) {
      setError(errorText(result.error ?? "POS_SHIFT_OPEN_FAILED"));
      setShiftOpenForm((current) => ({ ...current, pin: "" }));
      if (result.error?.includes("POS_TERMINAL_ALREADY_HAS_ACTIVE_SHIFT")) {
        await refreshShiftRuntime();
      }
      setBusy(false);
      return;
    }

    await saveCoreShift(result.data.active_shift);
    setShiftState({
      shift: result.data.active_shift,
      source: "CORE",
      verifiedAt: new Date().toISOString(),
    });
    setShiftOpenForm({ openingCash: "", pin: "" });
    setShowShiftOpen(false);
    setBusy(false);
  }

  if (!localStoreReady || localStoreBootError) {
    return (
      <main className="app-shell">
        <section className="topbar">
          <div className="brand-inline"><span className="brand-mark brand-mark--small">M</span><div><b>MORE POS</b><small>POS Runtime</small></div></div>
          <div className={`network-pill ${online ? "network-pill--online" : "network-pill--offline"}`}>{online ? "Online" : "Offline"}</div>
        </section>

        <section className="hero-panel hero-panel--compact" aria-live="polite">
          <div>
            <p className="eyebrow">POS RUNTIME</p>
            <h1>{localStoreBootError ? "เปิดข้อมูลเครื่องไม่สำเร็จ" : "กำลังเตรียม POS…"}</h1>
            <p className="hero-copy">
              {localStoreBootError
                ? "ระบบจะไม่เปิดหน้า Activate อัตโนมัติ เพื่อป้องกันการผูกเครื่องซ้ำ กรุณารีเฟรช POS หรือปิดแล้วเปิดใหม่"
                : "กำลังอ่าน Trusted Terminal, พนักงาน และกะล่าสุดจาก Local Runtime"}
            </p>
            <p className="form-note" data-pos-local-boot-stage>{localStoreBootStage}</p>
          </div>
          <div className="gate-badge">{localStoreBootError ? "RETRY" : "LOADING"}</div>
        </section>
      </main>
    );
  }

  const capabilities = hardware.getCapabilities();
  const trusted = Boolean(terminal?.identity) && (
    terminalVerification === "verified" ||
    terminalVerification === "cached" ||
    terminalVerification === "checking"
  );
  const activeShift = shiftState?.shift ?? null;
  const canOpenShift = actor?.permission_codes.includes("pos.shift.open") ?? false;

  const checks: RuntimeCheck[] = [
    {
      label: "Local Runtime",
      detail: activeShift
        ? `จำกะ ${activeShift.shift_status} ใน IndexedDB แล้ว · ${shiftState?.source === "LOCAL" ? "Local recovery" : "Core verified"}`
        : localStoreReady
          ? "IndexedDB พร้อมสำหรับ Terminal + Employee Session + POS Shift Snapshot"
          : "กำลังเปิด Durable Local Store",
      state: localStoreReady ? "ready" : "checking",
    },
    {
      label: "POS Gateway",
      detail: gatewayBaseUrl ? "MORE-owned Gateway ถูกกำหนดแล้ว" : "ยังไม่ได้กำหนด Gateway สำหรับ Runtime นี้",
      state: gatewayBaseUrl ? "ready" : "attention",
    },
    {
      label: "Trusted Terminal",
      detail: trusted
        ? `${terminal?.identity?.node_name} · ${terminal?.identity?.branch_name}`
        : terminalVerification === "checking"
          ? "กำลังอ่าน Terminal กลับจาก Owner Contract"
          : terminalVerification === "cached"
            ? "พบข้อมูล Local แต่ยังรอ Online Read-back"
            : "ยังไม่ผ่าน Terminal Activation",
      state: trusted ? "ready" : terminalVerification === "checking" ? "checking" : "attention",
    },
    {
      label: "Employee Session",
      detail: actor
        ? `${actor.employee_name} · ${sessionVerification === "verified" ? "Core verified" : sessionVerification === "checking" ? "กำลังตรวจ Session" : "Local resume"}`
        : trusted
          ? "ยังไม่ได้ยืนยันพนักงาน"
          : "รอ Trusted Terminal",
      state: actor
        ? sessionVerification === "checking" || sessionVerification === "cached"
          ? "checking"
          : "ready"
        : "attention",
    },
    {
      label: "POS Shift",
      detail: activeShift
        ? `${activeShift.shift_status} · ${activeShift.responsible_employee_name}`
        : trusted
          ? "ยังไม่เปิดกะ แต่ไม่ Block การเข้า POS"
          : "รอ Trusted Terminal",
      state: activeShift?.shift_status === "OPEN" ? "ready" : "attention",
    },
  ];

  const heroTitle = !trusted
    ? "Terminal ต้อง Trusted ก่อนเข้า POS"
    : !actor
      ? "ยืนยันพนักงานเพื่อเข้า POS"
      : "เข้า POS แล้ว";

  return (
    <main className="app-shell">
      <section className="topbar">
        <div className="brand-inline"><span className="brand-mark brand-mark--small">M</span><div><b>MORE POS</b><small>POS Runtime</small></div></div>
        <div className={`network-pill ${online ? "network-pill--online" : "network-pill--offline"}`}>{online ? "Online" : "Offline"}</div>
      </section>

      <section className="hero-panel hero-panel--compact">
        <div>
          <p className="eyebrow">STEP 1.2B · EMPLOYEE RUNTIME SESSION</p>
          <h1>{heroTitle}</h1>
          <p className="hero-copy">PIN ใช้เฉพาะตอนยืนยันตัวตนและไม่ถูก Persist; ปิด Browser, Restart หรือไฟดับไม่เท่ากับ Logout เมื่อ Employee Session ยัง Eligible ส่วน POS Shift ยังคงเป็น State แยกต่างหาก</p>
        </div>
        <div className={`gate-badge ${actor ? "gate-badge--ready" : ""}`}>{actor ? "POS READY" : trusted ? "LOGIN" : "SETUP"}</div>
      </section>

      {error ? <div className="error-banner">{error}</div> : null}

      {runtimeConfig.developmentGatewayOverrideAllowed && !gatewayBaseUrl ? (
        <section className="setup-card setup-card--dev">
          <div><p className="eyebrow">LOCAL DEVELOPMENT ONLY</p><h2>กำหนด Gateway สำหรับการทดสอบ</h2><p>ค่า URL เก็บใน Browser Session เท่านั้น Production จะมาจาก Deployment Environment</p></div>
          <form onSubmit={applyDevelopmentGateway} className="inline-form">
            <input value={gatewayDraft} onChange={(event) => setGatewayDraft(event.target.value)} placeholder="https://..." inputMode="url" />
            <button type="submit">ใช้ Gateway นี้</button>
          </form>
        </section>
      ) : null}

      <section className="runtime-grid runtime-grid--compact" aria-label="WebPOS runtime status">
        {checks.map((check) => (
          <article className="runtime-card" key={check.label}>
            <div className="runtime-card__title"><StatusDot state={check.state} /><h2>{check.label}</h2></div>
            <p>{check.detail}</p>
          </article>
        ))}
      </section>

      {!trusted ? (
        <section className="workspace-panel">
          <div className="workspace-copy">
            <p className="eyebrow">TERMINAL ACTIVATION</p>
            <h2>ผูก Browser เครื่องนี้กับ Commerce Node</h2>
            <p>สร้าง Commerce Node ที่ Back Office ก่อน แล้วกรอก Organization + Node Code; Branch จะอ่านกลับจาก Core เท่านั้น</p>
            <div className="rule-chip">ไม่มีช่องเลือกสาขา</div>
          </div>
          <div className="form-card">
            <form onSubmit={submitTerminalActivation}>
              <label><span>Organization Code</span><input name="organizationCode" autoComplete="off" value={activation.organizationCode} onChange={(event) => setActivation({ ...activation, organizationCode: event.target.value })} /></label>
              <label><span>Node Code</span><input name="nodeCode" autoComplete="off" value={activation.nodeCode} onChange={(event) => setActivation({ ...activation, nodeCode: event.target.value })} /></label>
              <label><span>บัญชีผู้มีสิทธิ์ Activate</span><input name="username" autoComplete="username" value={activation.username} onChange={(event) => setActivation({ ...activation, username: event.target.value })} /></label>
              <label><span>รหัสผ่าน</span><input name="password" type="password" autoComplete="current-password" value={activation.password} onChange={(event) => setActivation({ ...activation, password: event.target.value })} /></label>
              <button ref={bindLegacyActivationButton} className="primary-action" type="button" data-pos-terminal-activation="issue" disabled={busy || !gatewayBaseUrl}>{busy ? "กำลังตรวจสอบ…" : "Activate Terminal"}</button>
              <p className="form-note" data-pos-activation-stage>{activationStage ?? ""}</p>
              {allowRotate ? <button className="secondary-action" type="button" disabled={busy} onClick={() => void activateTerminal("ROTATE")}>Re-activate และยกเลิก Credential เก่า</button> : null}
            </form>
          </div>
        </section>
      ) : !actor ? (
        <section className="workspace-panel workspace-panel--trusted">
          <div className="terminal-card">
            <p className="eyebrow">TRUSTED TERMINAL</p>
            <h2>{terminal?.identity?.node_name}</h2>
            <dl>
              <div><dt>Branch</dt><dd>{terminal?.identity?.branch_name} <small>{terminal?.identity?.branch_code}</small></dd></div>
              <div><dt>Node</dt><dd>{terminal?.identity?.node_code}</dd></div>
              <div><dt>Shift</dt><dd>{activeShift?.shift_status === "CLOSING" ? "กำลังปิดกะ" : activeShift ? "เปิดแล้ว" : "ยังไม่เปิด"}</dd></div>
            </dl>
            <p className="locked-note">Login พนักงานเป็นประตูเข้า POS; การเปิดกะไม่ใช่ Login Gate</p>
          </div>
          <div className="form-card">
            <form onSubmit={loginEmployee}>
              <p className="eyebrow">EMPLOYEE LOGIN</p>
              <h2>เข้า POS</h2>
              <label><span>รหัสพนักงาน</span><input required autoComplete="off" value={login.employeeCode} onChange={(event) => setLogin({ ...login, employeeCode: event.target.value })} /></label>
              <label><span>PIN</span><input required type="password" inputMode="numeric" autoComplete="off" value={login.pin} onChange={(event) => setLogin({ ...login, pin: event.target.value })} /></label>
              <button className="primary-action" type="submit" disabled={busy || !online}>{busy ? "กำลังตรวจสอบ…" : "เข้า POS"}</button>
              {!online ? <p className="form-note">Offline Employee Re-entry ยังไม่เปิดจนกว่า Offline Access Policy จะถูกล็อก</p> : null}
            </form>
          </div>
        </section>
      ) : terminal ? (
        <PosWorkspace
          actor={actor}
          terminal={terminal}
          activeShift={activeShift}
          shiftState={shiftState}
          shiftDraft={shiftOpenForm}
          showShiftOpen={false}
          busy={busy}
          online={online}
          canOpenShift={canOpenShift}
          onShiftDraftChange={setShiftOpenForm}
          onShowShiftOpen={() => { setError(null); setShowShiftOpen(true); }}
          onCancelShiftOpen={() => { setShiftOpenForm({ openingCash: "", pin: "" }); setShowShiftOpen(false); setError(null); }}
          onOpenShift={openShift}
          onRefreshShift={() => void refreshShiftRuntime()}
          onSwitchEmployee={() => void endEmployeeRuntimeSession("FAST_SWITCH")}
          onLogoutEmployee={() => void endEmployeeRuntimeSession("EXPLICIT_LOGOUT")}
        />
      ) : null}

      {showShiftOpen && actor && terminal && employeeSession?.sessionToken && gatewayBaseUrl ? (
        <ShiftOpenRuntimeModal
          open={showShiftOpen}
          gatewayBaseUrl={gatewayBaseUrl}
          actor={actor}
          terminal={terminal}
          sessionToken={employeeSession.sessionToken}
          online={online}
          onClose={() => {
            setShiftOpenForm({ openingCash: "", pin: "" });
            setShowShiftOpen(false);
            setError(null);
          }}
          onOpened={async () => {
            await refreshShiftRuntime();
            setShiftOpenForm({ openingCash: "", pin: "" });
            setError(null);
          }}
        />
      ) : null}
    </main>
  );
}
